import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, Calculator, ArrowLeft } from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculateInflation, formatCurrency } from "../../utils/calculations";
import ProductLayout from "@/common/components/ProductLayout";

export const InflationCalculator = () => {
  const navigate = useNavigate();
  const [infCurrent, setInfCurrent] = useState(100000);
  const [infRate, setInfRate] = useState(6.0);
  const [infYears, setInfYears] = useState(10);

  const infResult = useMemo(
    () => calculateInflation(infCurrent, infRate, infYears),
    [infCurrent, infRate, infYears]
  );

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                Inflation Calculator
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-10 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="rounded-xl bg-red-100 p-3">
                      <TrendingUp className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-secondary">
                        Inflation Calculator
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Future Value of Money
                      </p>
                    </div>
                  </div>
                  <div className="space-y-6">
                    <CompactCalculatorRow
                      label="Current Cost"
                      value={infCurrent}
                      onChange={setInfCurrent}
                      min={1000}
                      max={10000000}
                      step={1000}
                      prefix="₹"
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="Inflation Rate"
                      value={infRate}
                      onChange={setInfRate}
                      min={1}
                      max={15}
                      step={0.1}
                      suffix="%"
                      colorClass="text-red-600"
                    />

                    <CompactCalculatorRow
                      label="Time Period"
                      value={infYears}
                      onChange={setInfYears}
                      min={1}
                      max={50}
                      step={1}
                      suffix="Yrs"
                      colorClass="text-blue-600"
                    />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-border bg-muted/20">
                <CardContent className="p-8">
                  <div className="mb-8 text-center">
                    <p className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Future Cost
                    </p>
                    <h2 className="text-4xl font-black text-red-700">
                      {formatCurrency(infResult.futureValue)}
                    </h2>
                  </div>
                  <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                    <p className="text-center font-medium text-slate-600">
                      You will need{" "}
                      <span className="font-bold text-red-600">
                        {formatCurrency(infResult.difference)}
                      </span>{" "}
                      more to buy the same thing in {infYears} years.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
